import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@packages/api';

/**
 * Invalidates the client hub's Outstanding caches whose AR balance changed.
 *
 * Pass a `clientId` when the mutation cannot move value between clients (an
 * invoice payment, a write-off, a credit note - all bound to one invoice's
 * client). Omit it when the mutation can, or when several clients are touched
 * at once (activity reallocation, a bank-statement reconcile spanning
 * multiple invoices); the account-scoped prefix then covers every cached
 * client.
 */
export async function invalidateClientOutstandingCaches(
  queryClient: QueryClient,
  accountId: string,
  clientId?: string | null,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey:
        clientId == null
          ? queryKeys.clientOutstandingBalances(accountId)
          : queryKeys.clientOutstandingBalance(accountId, clientId),
    }),
    // The hub's overview carries Outstanding and Overdue as well, and is
    // account-wide whichever client moved (see `invalidateClientOverviewCaches`).
    invalidateClientOverviewCaches(queryClient, accountId),
  ]);
}

/**
 * Invalidates every read on the client and matter hub: the client and matter
 * overviews, the matters summary, the WIP entry count, and the timeline with
 * its chip counts (activities, invoices, payments, expenses and emails).
 *
 * Deliberately account-wide rather than keyed per client. One prefix covers
 * every money path (AR, WIP, trust, journals) and every matter writer without
 * each call site having to name the client it moved. The cost is that every
 * mounted hub read refetches after any financial mutation, including each
 * loaded page of the matters summary, because a changed figure can reorder
 * the rows wholesale. Unmounted hubs are only marked stale.
 *
 * Many helpers here include this call and one user action routinely runs
 * several of them (recording a payment clears Outstanding, the ledger reports
 * and the activity figures). `cancelRefetch: false` lets the later calls join
 * the fetch the first one started instead of cancelling and restarting it, so
 * the hub refetches once. Every caller runs after the write has succeeded, so
 * the in-flight fetch already sees the new figures.
 */
export function invalidateClientOverviewCaches(queryClient: QueryClient, accountId: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: queryKeys.clientOverviewPrefix(accountId) }, { cancelRefetch: false });
}

/**
 * Invalidates a client's live trust balance, and the hub overview that shows
 * it, after anything that posts to or reverses a TRUST ledger allocation.
 *
 * Pass the `clientId` the posting names. Omit it only when the call site
 * cannot name one; the account-wide prefix then clears every client's balance.
 * This is the only place `clientTrustBalance` is invalidated: a call site that
 * does it inline leaves the hub overview showing the old trust figure.
 */
export async function invalidateClientTrustCaches(
  queryClient: QueryClient,
  accountId: string,
  clientId?: string | null,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey:
        clientId == null
          ? queryKeys.clientTrustBalance(accountId, '__account-wide-fallback__').slice(0, 2)
          : queryKeys.clientTrustBalance(accountId, clientId),
    }),
    invalidateClientOverviewCaches(queryClient, accountId),
  ]);
}

/**
 * Invalidates every cache holding a per-bank-account metric figure - the
 * confirmed balance, the allocation backlog, and the imported-statement
 * coverage - after a mutation that can move any of them.
 *
 * TWO cache namespaces hold the same three figures and BOTH must be cleared:
 *
 * - The per-bank single-id keys, read by the bank-account DETAIL page (via
 *   `useBankAccountMetrics`) and by the statement surfaces.
 * - The account-scoped BATCH prefixes, read by the bank-accounts LIST page,
 *   which fetches all three figures for every bank in one request each.
 *
 * The list moved onto the batch keys when it was rewritten to paint in one
 * settled pass. Every reconcile, match, payment and statement mutation already
 * invalidated the single keys, so before this helper existed those mutations
 * silently stopped refreshing the list: a user could allocate a line and return
 * to a list still showing the pre-allocation backlog and balance. Prefixes are
 * used for the batch side because the batch key embeds the full sorted
 * bank-account id array, so a caller cannot reconstruct the exact key the list
 * happens to be using.
 *
 * `bankAccountId` narrows the single-id half rather than gating it: pass it when
 * the mutation is bound to one bank account (the common case - a line
 * allocation, a statement approval) and only that bank's three keys clear; omit
 * it when the mutation cannot name one (a void whose funding bank is not known
 * client-side, a create) and the account-scoped prefixes clear every bank's.
 * Either way both namespaces are covered. Do not hand-roll this key set at a
 * call site.
 */
export async function invalidateBankAccountMetricCaches(
  queryClient: QueryClient,
  accountId: string,
  bankAccountId?: string,
): Promise<void> {
  await Promise.all([
    // Naming a bank narrows the single-id half to that bank's three keys;
    // omitting it widens to the account-scoped prefix, so every bank's figures
    // clear. The omit path must NOT skip this half - a mutation that cannot
    // name its bank still moved that bank's figures, and leaving the detail
    // page stale is the same defect this helper exists to prevent, one level
    // down.
    ...(bankAccountId === undefined
      ? [
          queryClient.invalidateQueries({
            queryKey: queryKeys.bankAccountBalanceSummaryPrefix(accountId),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.bankAccountUnallocatedCountPrefix(accountId),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.bankAccountImportedLineCoveragePrefix(accountId),
          }),
        ]
      : [
          queryClient.invalidateQueries({
            queryKey: queryKeys.bankAccountBalanceSummary(accountId, bankAccountId),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.bankAccountUnallocatedCount(accountId, bankAccountId),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.bankAccountImportedLineCoverage(accountId, bankAccountId),
          }),
        ]),
    queryClient.invalidateQueries({
      queryKey: queryKeys.bankAccountConfirmedBalancesPrefix(accountId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.bankAccountUnallocatedCountsPrefix(accountId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.bankAccountImportedLineCoverageBatchPrefix(accountId),
    }),
  ]);
}

/**
 * Invalidates every cache whose financial summary can change when an activity
 * is created, edited, approved, archived, invoiced, or imported.
 *
 * Detail queries intentionally use account-scoped prefixes because an edit can
 * move an activity between matters or clients. Client identity and Outstanding
 * are separate cache namespaces and remain untouched.
 */
export async function invalidateActivityFinancialCaches(
  queryClient: QueryClient,
  accountId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.clientProjectActivities(accountId),
    }),
    ...activitySummaryInvalidations(queryClient, accountId),
  ]);
}

/**
 * The summary-figure half of `invalidateActivityFinancialCaches`, without the
 * `clientProjectActivities` list itself. A mutation that only changes one
 * row's shape (approving a draft) patches that row directly instead of
 * refetching every loaded page of a `useInfiniteQuery` - see
 * `.claude/rules/no-blanket-invalidate-infinite-query.md` - but still needs
 * to refresh the summary figures (dashboard totals, matter/client WIP) the
 * approval moved. A mutation that can change which rows belong in the list
 * at all (create, or an edit that can move an activity between matters)
 * should keep using `invalidateActivityFinancialCaches` above.
 */
export function activitySummaryInvalidations(
  queryClient: QueryClient,
  accountId: string,
): Promise<void>[] {
  return [
    queryClient.invalidateQueries({
      queryKey: queryKeys.dashboardTotals(accountId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.clientProjects(accountId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.clientProjectPrefix(accountId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.clientWipTotals(accountId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.clientProjectWipTotals(accountId),
    }),
    // WIP, the entry count under it and the matters' last activity.
    invalidateClientOverviewCaches(queryClient, accountId),
  ];
}

/**
 * Every ledger-scoped key family a balance-changing mutation can move: trial
 * balance (flat, its multi-date series, and grouped), income statement
 * (grouped, both the single-period and multi-period series read), balance sheet
 * (grouped and its multi-date series), cash flow, the cashbook, trust creditors
 * (single-date and multi-date), both age analyses, ledger entry sets, and
 * ledger accounts.
 *
 * **A series read is a separate family from its single-date sibling**, and each
 * one has to be listed. They are different cache namespaces returning different
 * shapes, so `trialBalancePrefix` does not reach `trialBalanceSeries` - a
 * multi-date report left off this list keeps rendering pre-mutation figures
 * until its page is remounted, which is exactly the class of staleness this
 * list exists to prevent. The
 * ungrouped income-statement/balance-sheet reads have no consumer left in the
 * app - the account-groups feature fully replaced them - so there is no
 * `*Prefix` helper for either here.
 *
 * `invalidateLedgerReportCaches` below drives BOTH its ledger-scoped branch
 * and its account-wide fallback branch off this one list, precisely so a
 * renamed or added key family cannot update one branch and silently drift
 * from the other - which is what happened before this list existed: the
 * fallback held its own hand-copied literal per family.
 *
 * Supplier AP statements are not a family here: `supplierStatementPrefix`
 * is `(accountId, supplierId)`, and stuffing it in with a dummy ledgerId
 * would never prefix-match a cached statement. That companion lives on
 * `invalidateSupplierStatementCaches`, which `invalidateLedgerReportCaches`
 * calls next to this list.
 */
export const LEDGER_REPORT_KEY_FNS = [
  queryKeys.trialBalancePrefix,
  queryKeys.trialBalanceSeriesPrefix,
  queryKeys.groupedTrialBalancePrefix,
  queryKeys.groupedIncomeStatementPrefix,
  queryKeys.groupedIncomeStatementSeriesPrefix,
  queryKeys.groupedBalanceSheetPrefix,
  queryKeys.groupedBalanceSheetSeriesPrefix,
  queryKeys.cashFlowPrefix,
  queryKeys.cashFlowSeriesPrefix,
  queryKeys.indirectCashFlowPrefix,
  queryKeys.indirectCashFlowSeriesPrefix,
  queryKeys.cashbookReportPrefix,
  queryKeys.trustCreditorsPrefix,
  queryKeys.trustCreditorsSeriesPrefix,
  queryKeys.ageAnalysisPrefix,
  queryKeys.ledgerEntrySets,
  queryKeys.ledgerAccounts,
] as const satisfies ReadonlyArray<(accountId: string, ledgerId: string) => readonly unknown[]>;

/**
 * Shared report/ledger cache invalidation for any balance-changing mutation:
 * posting or reversing a manual journal, importing a bank statement or a
 * ledger-accounts CSV, or reallocating/approving an expense. Every one of
 * these can move any of the `LEDGER_REPORT_KEY_FNS` families, so a mutation
 * that invalidates only some of them leaves the others rendering stale
 * figures.
 *
 * Lives here rather than under `routes/ledger` because its call sites are not
 * ledger-only: expense mutations (`routes/expenses/expense-invalidation.ts`), the bulk chart-of-accounts CSV importer
 * (`components/bulk-import/CsvImportDialog.tsx`), and the expense-payment
 * dialog (`components/payment/RecordExpensePaymentDialog.tsx`) all call it
 * alongside the journal-posting form under `routes/ledger`.
 *
 * Pass `ledgerId` when the call site has it: each key family's full
 * ledger-scoped key (or `*Prefix` for the four that vary by report period /
 * as-at date / showZeroBalances) then invalidates just that ledger's cache
 * entries. Omit it when the call site only reliably knows the account (some
 * expense mutations do not have a resolved `ledgerId` at `onSuccess` time -
 * see `expense-invalidation.ts`); the fallback branch below invalidates the
 * account-wide 2-segment prefix (`[keyName, accountId]`) for every family
 * instead, a strict superset covering every ledger on the account - derived
 * by calling the same `LEDGER_REPORT_KEY_FNS` with a placeholder ledgerId and
 * slicing off the ledger segment, rather than kept as a parallel literal list.
 *
 * Also clears every cached supplier AP statement on the account. Recording a
 * payment, approving, or voiding an expense posts or reverses an AP journal,
 * and a manual journal can move AP for any supplier; the 30s app-wide
 * `staleTime` would otherwise keep the supplier-detail statement showing
 * pre-mutation figures. The statement key is supplier-scoped, so this is a
 * sibling of the ledger-family loop, not an extra `LEDGER_REPORT_KEY_FNS`
 * entry. No `supplierId` is known here (the expense helpers and the payment
 * dialog already omit `ledgerId` for the same reason), so the account-wide
 * two-segment prefix covers every supplier and period.
 */
export function invalidateLedgerReportCaches(
  queryClient: QueryClient,
  accountId: string,
  ledgerId?: string,
): void {
  invalidateLedgerKeyFamilies(queryClient, LEDGER_REPORT_KEY_FNS, accountId, ledgerId);
  void invalidateSupplierStatementCaches(queryClient, accountId);
  // A journal, reversal, trust opening balance or CSV import can move any
  // client's Outstanding or trust figure on the hub.
  void invalidateClientOverviewCaches(queryClient, accountId);
}

/**
 * Invalidates every cached supplier AP statement period after a mutation that
 * can move Accounts Payable.
 *
 * `supplierStatement` keys always carry `from`/`to` slots, so calling the
 * full key with those omitted would pass explicit `undefined` and fail
 * TanStack Query's prefix match against a cached key holding real date
 * strings. The prefix stops at `supplierId` and therefore reaches every
 * period variant - the same reason `clientStatementFeedsPrefix` exists on
 * the AR side after a payment, write-off, or invoice correction.
 *
 * Pass `supplierId` when the mutation is bound to one supplier. Omit it when
 * several may have moved, or when the call site has no supplier in scope (a
 * journal, a CSV import, `invalidateLedgerReportCaches`); the account-wide
 * two-segment prefix is derived by slicing the supplier-scoped prefix, the
 * same placeholder-and-slice used for ledger families, rather than a
 * parallel account-only key.
 */
export function invalidateSupplierStatementCaches(
  queryClient: QueryClient,
  accountId: string,
  supplierId?: string,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey:
      supplierId === undefined
        ? queryKeys.supplierStatementPrefix(accountId, '__account-wide-fallback__').slice(0, 2)
        : queryKeys.supplierStatementPrefix(accountId, supplierId),
  });
}

/**
 * Invalidates every cached client statement feed period (all `from`/`to`
 * variants, via `clientStatementFeedsPrefix`) after a mutation that moves a
 * client's Accounts Receivable. The AR counterpart of
 * `invalidateSupplierStatementCaches`.
 *
 * Pass `clientId` when the mutation is bound to one client. Omit it when the
 * call site has no client in scope (an unmatch of a bank line, which does not
 * carry the client); the account-wide two-segment prefix is derived by slicing
 * the client-scoped prefix, the same placeholder-and-slice as the supplier
 * helper.
 */
export function invalidateClientStatementCaches(
  queryClient: QueryClient,
  accountId: string,
  clientId?: string,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey:
      clientId === undefined
        ? queryKeys.clientStatementFeedsPrefix(accountId, '__account-wide-fallback__').slice(0, 2)
        : queryKeys.clientStatementFeedsPrefix(accountId, clientId),
  });
}

/**
 * Every family in `LEDGER_REPORT_KEY_FNS` except the journals list, for a
 * mutation that changes a ledger ACCOUNT's own properties rather than posting
 * anything: a rename, a code or section change, a VAT treatment, a cash flow
 * classification, a group move, an archive.
 *
 * The journals list is excluded because such an edit does not touch it.
 * `queryKeys.ledgerEntrySets` backs the `useInfiniteQuery` on the GL account
 * page - the very page these edits are made from - so invalidating it turns
 * one save into a refetch of every page the user has scrolled through
 * (`.claude/rules/no-blanket-invalidate-infinite-query.md`). Nothing in the
 * response would change if it did: an entry set carries only ids, amounts and
 * its own description, never an account's name, code or classification, and
 * the leg rows resolve their labels (and the Archived badge) from the chart of
 * accounts through `JournalLegTable`'s `accountNameMap`. That map is built from
 * the archived-inclusive read, `queryKeys.ledgerAccountsIncludingArchived`,
 * which is not a family of its own: its key extends `ledgerAccounts`' with a
 * fourth `'includingArchived'` segment precisely so an invalidation of
 * `ledgerAccounts` prefix-matches it (React Query matches partially from the
 * start unless `exact` is set). Invalidating `ledgerAccounts` here therefore
 * refreshes those labels too - the list they annotate just does not have to be
 * refetched to do it. Membership is untouched too: the list is filtered by
 * ledger account id, and none of these edits moves an entry between accounts.
 *
 * Anything that POSTS or REVERSES a journal changes which rows belong in that
 * list, so it keeps calling `invalidateLedgerReportCaches` above.
 */
export function invalidateLedgerAccountPropertyCaches(
  queryClient: QueryClient,
  accountId: string,
  ledgerId?: string,
): void {
  invalidateLedgerKeyFamilies(
    queryClient,
    LEDGER_REPORT_KEY_FNS.filter((keyFn) => keyFn !== queryKeys.ledgerEntrySets),
    accountId,
    ledgerId,
  );
  // Archiving or re-sectioning an account changes which fee earner mappings
  // still count as effective.
  queryClient.invalidateQueries({ queryKey: queryKeys.feeEarnerFeeAccounts(accountId) });
}

/**
 * The shared loop both helpers above run: each family's ledger-scoped key when
 * a `ledgerId` is known, else the account-wide 2-segment prefix covering every
 * ledger on the account. Deriving the fallback by slicing a placeholder key
 * rather than keeping a parallel literal list is what stops the two branches
 * drifting when a family is renamed or added.
 */
function invalidateLedgerKeyFamilies(
  queryClient: QueryClient,
  keyFns: ReadonlyArray<(accountId: string, ledgerId: string) => readonly unknown[]>,
  accountId: string,
  ledgerId?: string,
): void {
  for (const keyFn of keyFns) {
    const key = ledgerId
      ? keyFn(accountId, ledgerId)
      : keyFn(accountId, '__account-wide-fallback__').slice(0, 2);
    queryClient.invalidateQueries({ queryKey: key });
  }
}

/**
 * Invalidates the cashbook report after a supporting document is attached to,
 * or removed from, a journal, a bank-statement line, or a supplier invoice
 * (expense).
 *
 * The cashbook's voucher column is a count computed server-side per movement,
 * over all three document parent types the report can reach:
 * `LEDGER_ENTRY_SET` for a journal's own attachments, `BANK_STATEMENT_LINE`
 * for the line that produced the movement, and `SUPPLIER_INVOICE` for an
 * expense (`CashbookMovementSchema.supplierInvoiceIds` in `@packages/types`
 * for how that set is derived). None of those document surfaces renders
 * the cashbook, so a mutation on any one of them has no local signal that it
 * just moved a figure on another page - which is exactly how the count went
 * stale: a voucher attached from the journals tab, a bank line, or an
 * expense left the cashbook showing its pre-upload count, and the chase list
 * (movements with no voucher) kept naming a movement whose evidence had just
 * been filed, on a report whose purpose is evidencing the record of receipts
 * and payments.
 *
 * The ledger-scoped prefix is invalidated rather than one row patched: the
 * count is derived server-side and the mutation returns only the document, so
 * there is no updated movement to patch in. That is cheap despite the cashbook
 * being an infinite query, because every caller lives on another page -
 * React Query's default `refetchType: 'active'` marks an unmounted query
 * stale without refetching it. See
 * `.claude/rules/no-blanket-invalidate-infinite-query.md`.
 *
 * `ledgerId` narrows to one ledger's cache; omit it for the account-wide
 * prefix, which every ledger's cashbook still prefix-matches. The expense
 * page (`SupplierInvoiceDocuments`) has no ledger in scope for the expense it
 * is editing, so it always omits it - free today because no surface that
 * mounts `SupplierInvoiceForm` (`expense-new.tsx`, `expense-detail.tsx`,
 * `EmailAttachmentExpenseCreate.tsx`) also renders `CashbookPage`; a future
 * affordance to file an expense directly from a cashbook row would need to
 * re-check that.
 */
export function invalidateCashbookVoucherCaches(
  queryClient: QueryClient,
  accountId: string,
  ledgerId?: string,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: ledgerId
      ? queryKeys.cashbookReportPrefix(accountId, ledgerId)
      : queryKeys.cashbookReportPrefix(accountId, '__account-wide-fallback__').slice(0, 2),
  });
}

/**
 * Marks stale every reader that shows a supplier invoice's documents from
 * another record: the cashbook voucher column and each asset's document list
 * (a sourced asset lists its source invoice's documents). Account-wide
 * prefixes, because the expense page does not know which asset it sourced.
 */
export function invalidateSupplierInvoiceDocumentReaderCaches(
  queryClient: QueryClient,
  accountId: string,
): Promise<void> {
  return Promise.all([
    invalidateCashbookVoucherCaches(queryClient, accountId),
    queryClient.invalidateQueries({ queryKey: queryKeys.assetSupportingDocumentsPrefix(accountId) }),
  ]).then(() => undefined);
}

/**
 * Marks stale everything that reads a bank statement line's supporting
 * documents, after one is attached or deleted: the line's own document list,
 * the per-row paperclip counts, and the cashbook voucher column.
 */
export function invalidateBankStatementLineDocumentCaches(
  queryClient: QueryClient,
  { accountId, ledgerId, lineId }: { accountId: string; ledgerId: string; lineId: string },
): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.bankStatementLineDocuments(accountId, lineId) }),
    // Prefix key: each page's count query appends its own lineIds array, so
    // this marks every cached count page stale at once.
    queryClient.invalidateQueries({ queryKey: queryKeys.bankStatementLineDocumentCounts(accountId) }),
    invalidateCashbookVoucherCaches(queryClient, accountId, ledgerId),
  ]).then(() => undefined);
}
